import { useState, useCallback, useRef, useEffect } from 'react'
import {
  createPlaidLinkToken,
  linkPlaidAccount,
  deletePlaidAccount,
  optOutPlaid,
  getNinjaConfig,
  createNinjaLinkToken,
  linkNinjaAccount,
  listNinjaAccounts,
  deleteNinjaAccount,
  optOutNinja,
  uploadNoBankNinja,
  type PlaidAccount,
  type NinjaAccount,
  type NinjaConfig as NinjaConfigType,
} from '@/lib/api/bankService'
import {
  loadPlaidSDK,
  loadNinjaSDK,
  isPlaidSDKLoaded,
  isNinjaSDKLoaded,
  type PlaidHandler,
} from '@/lib/utils/bankLinking'

interface UseBankLinkingOptions {
  applicantId: string | number | null
  onSuccess?: (type: 'plaid' | 'ninja' | 'upload' | 'remove_plaid' | 'remove_ninja' | 'opt_out_plaid' | 'opt_out_ninja') => void
  onError?: (error: string) => void
}

interface BankLinkingState {
  isLoading: boolean
  error: string | null
  plaidAccounts: PlaidAccount[]
  ninjaAccounts: NinjaAccount[]
  ninjaConfig: NinjaConfigType | null
  plaidOptOut: boolean
  ninjaOptOut: boolean
}

/**
 * Custom hook for bank linking functionality
 * Handles Plaid and Ninja bank account linking with proper state management
 */
export function useBankLinking({
  applicantId,
  onSuccess,
  onError,
}: UseBankLinkingOptions) {
  const [state, setState] = useState<BankLinkingState>({
    isLoading: false,
    error: null,
    plaidAccounts: [],
    ninjaAccounts: [],
    ninjaConfig: null,
    plaidOptOut: false,
    ninjaOptOut: false,
  })

  const plaidHandlerRef = useRef<PlaidHandler | null>(null)
  const elementRef = useRef<HTMLDivElement | null>(null)
  const ninjaWidgetMounted = useRef(false)

  // Load Ninja config on mount
  useEffect(() => {
    if (!applicantId) return

    let mounted = true

    const loadConfig = async () => {
      try {
        const config = await getNinjaConfig()
        if (mounted) {
          setState((prev) => ({ ...prev, ninjaConfig: config }))
        }
      } catch (error) {
        // Silently fail - config is optional
        console.error('Failed to load Ninja config:', error)
      }
    }

    loadConfig()

    return () => {
      mounted = false
    }
  }, [applicantId])

  // Load accounts on mount
  useEffect(() => {
    if (!applicantId) return

    const loadAccounts = async () => {
      try {
        const ninjaAccounts = await listNinjaAccounts(applicantId).catch(() => [])

        setState((prev) => ({
          ...prev,
          plaidAccounts: [], // Plaid accounts API not used
          ninjaAccounts,
        }))
      } catch (error) {
        // Silently fail - accounts will be loaded when needed
        console.error('Failed to load accounts:', error)
      }
    }

    loadAccounts()
  }, [applicantId])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (plaidHandlerRef.current) {
        try {
          plaidHandlerRef.current.destroy()
        } catch {
          // Ignore cleanup errors
        }
        plaidHandlerRef.current = null
      }

      // Cleanup Ninja widget
      if (ninjaWidgetMounted.current && window.NinjaFetchWidget?.unmount) {
        try {
          window.NinjaFetchWidget.unmount()
        } catch {
          // Ignore cleanup errors
        }
        ninjaWidgetMounted.current = false
      }
    }
  }, [])

  /**
   * Initialize and open Plaid Link
   */
  const openPlaidLink = useCallback(async () => {
    if (!applicantId) {
      onError?.('Applicant ID is required')
      return
    }

    if (state.plaidOptOut) {
      onError?.('You have opted out of Plaid bank linking')
      return
    }

    setState((prev) => ({ ...prev, isLoading: true, error: null }))

    try {
      // Create element ref if not exists
      if (!elementRef.current) {
        const div = document.createElement('div')
        div.style.display = 'none'
        document.body.appendChild(div)
        elementRef.current = div
      }

      // Load Plaid SDK
      await loadPlaidSDK(elementRef.current)

      if (!isPlaidSDKLoaded() || !window.Plaid) {
        throw new Error('Plaid SDK failed to load')
      }

      // Generate link token
      const linkToken = await createPlaidLinkToken()

      // Cleanup previous handler
      if (plaidHandlerRef.current) {
        try {
          plaidHandlerRef.current.destroy()
        } catch {
          // Ignore cleanup errors
        }
      }

      // Create Plaid handler
      const handler = window.Plaid.create({
        token: linkToken,
        onSuccess: async (publicToken: string) => {
          try {
            await linkPlaidAccount(applicantId, publicToken)

            // Plaid accounts API not used - keep empty array
            setState((prev) => ({ ...prev, plaidAccounts: [] }))

            onSuccess?.('plaid')
            setState((prev) => ({ ...prev, isLoading: false }))
          } catch (error) {
            const errorMessage =
              error instanceof Error ? error.message : 'Failed to link account'
            onError?.(errorMessage)
            setState((prev) => ({
              ...prev,
              isLoading: false,
              error: errorMessage,
            }))
          }
        },
        onExit: (error) => {
          setState((prev) => ({ ...prev, isLoading: false }))
          if (error) {
            const errorMessage = error.error_message || 'Plaid link exited with error'
            onError?.(errorMessage)
          }
        },
        onEvent: () => {
          // Track events if needed
        },
      })

      plaidHandlerRef.current = handler
      handler.open()
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to initialize Plaid Link'
      onError?.(errorMessage)
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }))
    }
  }, [applicantId, state.plaidOptOut, onSuccess, onError])

  /**
   * Initialize and open Ninja widget
   */
  const openNinjaWidget = useCallback(async () => {
    if (!applicantId) {
      onError?.('Applicant ID is required')
      return
    }

    if (state.ninjaOptOut) {
      onError?.('You have opted out of Ninja bank linking')
      return
    }

    if (!state.ninjaConfig) {
      onError?.('Ninja configuration not available')
      return
    }

    setState((prev) => ({ ...prev, isLoading: true, error: null }))

    try {
      // Create element ref if not exists
      if (!elementRef.current) {
        const div = document.createElement('div')
        div.style.display = 'none'
        document.body.appendChild(div)
        elementRef.current = div
      }

      // Load Ninja SDK - use NINJA_JS_URL_V2 if available, fallback to NINJA_JS_URL
      const ninjaJsUrl = state.ninjaConfig.NINJA_JS_URL_V2 || state.ninjaConfig.NINJA_JS_URL
      if (!ninjaJsUrl) {
        throw new Error('Ninja JS URL not configured')
      }
      await loadNinjaSDK(ninjaJsUrl, elementRef.current)

      if (!isNinjaSDKLoaded() || !window.NinjaFetchWidget) {
        throw new Error('Ninja SDK failed to load')
      }

      // Generate link token
      const linkToken = await createNinjaLinkToken(applicantId)

      // Return config for widget mounting (should be done in component)
      return {
        linkToken,
        jsUrl: ninjaJsUrl,
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to initialize Ninja widget'
      onError?.(errorMessage)
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }))
      throw error
    }
  }, [applicantId, state.ninjaOptOut, state.ninjaConfig, onError])

  /**
   * Mount Ninja widget in a container
   */
  const mountNinjaWidget = useCallback(
    async (
      containerId: string,
      consumerData: {
        first_name: string
        last_name: string
        reference_date?: string
      },
      providedToken?: string | null
    ) => {
      if (!applicantId) {
        onError?.('Applicant ID is required')
        return
      }

      if (!state.ninjaConfig) {
        onError?.('Ninja configuration not available')
        return
      }

      // Validate that first_name and last_name are provided and not empty
      if (!consumerData.first_name || consumerData.first_name.trim() === '') {
        const errorMessage = 'First name is required to link your bank account. Please complete your profile first.'
        onError?.(errorMessage)
        setState((prev) => ({ ...prev, error: errorMessage }))
        return
      }

      if (!consumerData.last_name || consumerData.last_name.trim() === '') {
        const errorMessage = 'Last name is required to link your bank account. Please complete your profile first.'
        onError?.(errorMessage)
        setState((prev) => ({ ...prev, error: errorMessage }))
        return
      }

      setState((prev) => ({ ...prev, isLoading: true, error: null }))

      try {
        // Create element ref if not exists
        if (!elementRef.current) {
          const div = document.createElement('div')
          div.style.display = 'none'
          document.body.appendChild(div)
          elementRef.current = div
        }

        // Load Ninja SDK if not already loaded
        if (!isNinjaSDKLoaded() || !window.NinjaFetchWidget) {
          const jsUrl = state.ninjaConfig.NINJA_JS_URL_V2 || state.ninjaConfig.NINJA_JS_URL
          if (!jsUrl) {
            throw new Error('Ninja JS URL not configured')
          }
          await loadNinjaSDK(jsUrl, elementRef.current)

          if (!isNinjaSDKLoaded() || !window.NinjaFetchWidget) {
            throw new Error('Ninja SDK failed to load')
          }
        }

        // Unmount previous widget if exists
        if (ninjaWidgetMounted.current && window.NinjaFetchWidget?.unmount) {
          try {
            window.NinjaFetchWidget.unmount()
          } catch {
            // Ignore unmount errors
          }
          ninjaWidgetMounted.current = false
        }

        // Wait for container element to exist in DOM
        let containerElement = document.getElementById(containerId)
        let retries = 0
        const maxRetries = 10
        while (!containerElement && retries < maxRetries) {
          await new Promise((resolve) => setTimeout(resolve, 100))
          containerElement = document.getElementById(containerId)
          retries++
        }

        if (!containerElement) {
          throw new Error(`Container element with id "${containerId}" not found`)
        }

        // Use provided token if available, otherwise generate a new one
        const linkToken = providedToken || await createNinjaLinkToken(applicantId)

        // IMPORTANT: consumer_id in consumer_data must match the consumer_id in the token
        // Backend now generates tokens with LR format
        const widgetConfig = {
          access_token: linkToken,
          container_id: containerId,
          consumer_data: {
            consumer_id: `LR${applicantId}`, // Must match token's consumer_id (LR format)
            first_name: consumerData.first_name.trim(),
            last_name: consumerData.last_name.trim(),
            reference_date: consumerData.reference_date || new Date().toISOString().split('T')[0],
          },
          onSuccess: async (data: { product_request_id?: string; request_id?: string }) => {
            try {
              const publicToken = data.product_request_id || data.request_id || ''
              if (!publicToken) {
                throw new Error('No product request ID or request ID received')
              }

              await linkNinjaAccount(applicantId, publicToken)

              // Reload accounts
              const accounts = await listNinjaAccounts(applicantId)
              setState((prev) => ({ ...prev, ninjaAccounts: accounts }))

              onSuccess?.('ninja')
              setState((prev) => ({ ...prev, isLoading: false }))
            } catch (error) {
              const errorMessage =
                error instanceof Error ? error.message : 'Failed to link account'
              onError?.(errorMessage)
              setState((prev) => ({
                ...prev,
                isLoading: false,
                error: errorMessage,
              }))
            }
          },
          onError: (data: { message?: string; status?: string }) => {
            const errorMessage = data.message || data.status || 'Ninja widget error'
            onError?.(errorMessage)
            setState((prev) => ({
              ...prev,
              isLoading: false,
              error: errorMessage,
            }))
          },
          onClose: () => {
            setState((prev) => ({ ...prev, isLoading: false }))
            ninjaWidgetMounted.current = false
          },
        }

        try {
          window.NinjaFetchWidget.mount(widgetConfig)
          ninjaWidgetMounted.current = true
        } catch (mountError: unknown) {
          const errorMessage =
            mountError instanceof Error
              ? mountError.message || 'Failed to mount'
              : mountError &&
                  typeof mountError === 'object' &&
                  'message' in mountError &&
                  typeof (mountError as { message: unknown }).message === 'string'
                ? (mountError as { message: string }).message || 'Failed to mount'
                : 'Failed to mount'

          // Handle "already mounted" error specifically (like Angular implementation)
          if (errorMessage.includes('EmbeddableWidget is already mounted') ||
            errorMessage.includes('unmount first')) {

            // Force unmount and retry
            if (window.NinjaFetchWidget?.unmount) {
              try {
                window.NinjaFetchWidget.unmount()
              } catch { /* ignore */ }

              ninjaWidgetMounted.current = false

              // Retry after short delay
              setTimeout(() => {
                try {
                  if (document.getElementById(containerId) && window.NinjaFetchWidget) {
                    window.NinjaFetchWidget.mount(widgetConfig)
                    ninjaWidgetMounted.current = true
                    setState((prev) => ({ ...prev, isLoading: false, error: null }))
                  }
                } catch (retryError) {
                  console.error('Retry mount failed', retryError)
                }
              }, 200)
              return
            }
          }
          throw mountError // Re-throw if not handled
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to mount Ninja widget'
        onError?.(errorMessage)
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: errorMessage,
        }))
      }
    },
    [applicantId, state.ninjaConfig, onSuccess, onError]
  )

  /**
   * Delete Plaid account
   */
  const removePlaidAccount = useCallback(
    async (pliId: number) => {
      if (!applicantId) {
        onError?.('Applicant ID is required')
        return
      }

      setState((prev) => ({ ...prev, isLoading: true, error: null }))

      try {
        await deletePlaidAccount(applicantId, pliId)
        setState((prev) => ({ ...prev, plaidAccounts: [], isLoading: false }))
        onSuccess?.('remove_plaid')
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to delete account'
        onError?.(errorMessage)
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: errorMessage,
        }))
      }
    },
    [applicantId, onSuccess, onError]
  )

  /**
   * Delete Ninja account
   */
  const removeNinjaAccount = useCallback(
    async (pliId: number) => {
      if (!applicantId) {
        onError?.('Applicant ID is required')
        return
      }

      setState((prev) => ({ ...prev, isLoading: true, error: null }))

      try {
        await deleteNinjaAccount(applicantId, pliId)
        const accounts = await listNinjaAccounts(applicantId)
        setState((prev) => ({ ...prev, ninjaAccounts: accounts, isLoading: false }))
        onSuccess?.('remove_ninja')
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to delete account'
        onError?.(errorMessage)
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: errorMessage,
        }))
      }
    },
    [applicantId, onSuccess, onError]
  )

  /**
   * Opt out of Plaid
   */
  const handleOptOutPlaid = useCallback(async () => {
    if (!applicantId) {
      onError?.('Applicant ID is required')
      return
    }

    setState((prev) => ({ ...prev, isLoading: true, error: null }))

    try {
      await optOutPlaid(applicantId)
      setState((prev) => ({ ...prev, plaidOptOut: true, isLoading: false }))
      onSuccess?.('opt_out_plaid')
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to opt out'
      onError?.(errorMessage)
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }))
    }
  }, [applicantId, onSuccess, onError])

  /**
   * Opt out of Ninja
   */
  const handleOptOutNinja = useCallback(async () => {
    if (!applicantId) {
      onError?.('Applicant ID is required')
      return
    }

    setState((prev) => ({ ...prev, isLoading: true, error: null }))

    try {
      await optOutNinja(applicantId)
      setState((prev) => ({ ...prev, ninjaOptOut: true, isLoading: false }))
      onSuccess?.('opt_out_ninja')
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to opt out'
      onError?.(errorMessage)
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }))
    }
  }, [applicantId, onSuccess, onError])

  /**
   * Upload bank documents (no bank linking)
   */
  const uploadBankDocuments = useCallback(
    async (bankName: string, files: File | File[]) => {
      if (!applicantId) {
        onError?.('Applicant ID is required')
        return
      }

      setState((prev) => ({ ...prev, isLoading: true, error: null }))

      try {
        await uploadNoBankNinja(applicantId, bankName, files)
        setState((prev) => ({ ...prev, isLoading: false }))
        onSuccess?.('upload')
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to upload documents'
        onError?.(errorMessage)
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: errorMessage,
        }))
        throw error
      }
    },
    [applicantId, onSuccess, onError]
  )

  /**
   * Refresh accounts list
   */
  const refreshAccounts = useCallback(async () => {
    if (!applicantId) return

    try {
      const ninjaAccounts = await listNinjaAccounts(applicantId).catch(() => [])

      setState((prev) => ({
        ...prev,
        plaidAccounts: [], // Plaid accounts API not used
        ninjaAccounts,
      }))
    } catch (error) {
      console.error('Failed to refresh accounts:', error)
    }
  }, [applicantId])

  /**
   * Unmount Ninja widget
   */
  const unmountNinjaWidget = useCallback(() => {
    if (ninjaWidgetMounted.current && window.NinjaFetchWidget?.unmount) {
      try {
        window.NinjaFetchWidget.unmount()
      } catch (error) {
        console.error('Error unmounting Ninja widget:', error)
      }
      ninjaWidgetMounted.current = false
    }

    // Reset loading state when unmounting
    setState((prev) => ({ ...prev, isLoading: false }))
  }, [])

  return {
    // State
    ...state,
    // Actions
    openPlaidLink,
    openNinjaWidget,
    mountNinjaWidget,
    unmountNinjaWidget,
    removePlaidAccount,
    removeNinjaAccount,
    handleOptOutPlaid,
    handleOptOutNinja,
    uploadBankDocuments,
    refreshAccounts,
  }
}
